import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';

type UseConfirmDialogBaseOptions = {
  onSuccess?: () => Promise<unknown> | unknown;
  successMessage?: string;
  errorMessage?: string;
};

export type UseConfirmDialogUncontrolledOptions<T> =
  UseConfirmDialogBaseOptions & {
    onConfirm: (item: T) => Promise<unknown> | unknown;
    open?: never;
    onClose?: never;
  };

export type UseConfirmDialogControlledOptions = UseConfirmDialogBaseOptions & {
  onConfirm: () => Promise<unknown> | unknown;
  open: boolean;
  onClose: () => void;
};

export type UseConfirmDialogOptions<T> =
  | UseConfirmDialogUncontrolledOptions<T>
  | UseConfirmDialogControlledOptions;

export interface ConfirmDialogState<T> {
  item: T | null;
  open: boolean;
  loading: boolean;
  request: (item: T) => void;
  cancel: () => void;
  confirm: () => Promise<void>;
}

export function useConfirmDialog<T>(
  options: UseConfirmDialogUncontrolledOptions<T>
): ConfirmDialogState<T>;
export function useConfirmDialog(
  options: UseConfirmDialogControlledOptions
): ConfirmDialogState<unknown>;
export function useConfirmDialog<T>(
  options: UseConfirmDialogOptions<T>
): ConfirmDialogState<T> {
  const { onSuccess, successMessage, errorMessage } = options;
  const [item, setItem] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);

  const isControlledOptions = <T>(
    options: UseConfirmDialogOptions<T>
  ): options is UseConfirmDialogControlledOptions => 'open' in options;
  const isControlled = isControlledOptions(options);
  const open = isControlled ? options.open : item != null;
  const onControlledConfirm = isControlled ? options.onConfirm : undefined;
  const onUncontrolledConfirm = isControlled ? undefined : options.onConfirm;
  const onClose = isControlled ? options.onClose : undefined;

  const request = useCallback(
    (next: T) => {
      if (!isControlled) {
        setItem(next);
      }
    },
    [isControlled]
  );

  const cancel = useCallback(() => {
    if (loading) return;
    if (onClose) {
      onClose();
      return;
    }
    setItem(null);
  }, [loading, onClose]);

  const confirm = useCallback(async () => {
    if (!open) return;
    if (!isControlled && !item) return;
    setLoading(true);
    try {
      if (onControlledConfirm) {
        await onControlledConfirm();
      } else if (item && onUncontrolledConfirm) {
        await onUncontrolledConfirm(item);
      }
      if (successMessage) toast.success(successMessage);
      await onSuccess?.();
      if (onClose) {
        onClose();
      } else {
        setItem(null);
      }
    } catch {
      if (errorMessage) toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [
    open,
    isControlled,
    item,
    onControlledConfirm,
    onUncontrolledConfirm,
    onSuccess,
    successMessage,
    errorMessage,
    onClose,
  ]);

  return {
    item,
    open,
    loading,
    request,
    cancel,
    confirm,
  };
}
